import instantsearch from 'instantsearch.js';
import { searchBox, configure } from 'instantsearch.js/es/widgets';
import { connectHits } from 'instantsearch.js/es/connectors';
import searchClient from './search-client';
import { HitHelpers } from './hit-helpers';
import { KEY_CODES } from '../../utils/constants';
import { createMiddleware } from './insights';

class InlineSearch {
  constructor(rootClassName, searchInputClassName, hitContainerClassName) {
    this.rootClassName = rootClassName;
    this.searchInputClassName = searchInputClassName;
    this.hitContainerClassName = hitContainerClassName;

    this.rootEl = document.querySelector(this.rootClassName);
    this.siteUrl = document.body.dataset['parentUrl'];
    this.customHits = connectHits(this.renderHits.bind(this));

    this.indexName = this.rootEl.dataset['algoliaIndex'];
    this.search = instantsearch({
      indexName: this.indexName,
      searchClient,
      onStateChange: ({ uiState, setUiState }) => {
        const hasValidQuery =
          uiState[this.indexName].query && uiState[this.indexName].query.trim().length > 0;
        this.rootEl.setAttribute('data-valid', !!hasValidQuery);
        setUiState(uiState);
      },
      insights: true,
      future: {
        preserveSharedStateOnUnmount: true,
      },
    });

    try {
      const insightsMiddleware = createMiddleware('Inline');
      this.search.use(insightsMiddleware);
    } catch (error) {
      console.error('Failed to initialize Algolia Insights middleware:', error);
    }

    this.init();
    this.search.start();
  }

  init() {
    this.search.addWidgets([
      configure({
        hitsPerPage: 5,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
      }),

      searchBox({
        container: this.searchInputClassName,
        placeholder: 'Search',
        showSubmit: false,
      }),

      this.customHits({
        container: this.hitContainerClassName,
        templates: {
          empty(results) {
            if (!results || !results.query.trim()) {
              return '';
            } else {
              return `<p class="hit-no-results">No results for <span>${results.query}</span></p>`;
            }
          },
          item(hit) {
            return `
              <div class="hit-article">
                <div class="hit-title">
                  <a class="hit-link" href="${hit.externalUrl || hit.url}" tabindex="0"${hit.type ==='press' ? ` target="_blank"` : ''}>
                    ${hit.formatted_title ? hit.formatted_title : hit.title}
                  </a>
                </div>
                <p class="hit-type">${HitHelpers.getTypeDisplay(hit.type)}</p>
                ${hit.thumbnail ? `
                  <div class="hit-thumbnail">
                    <img src="${hit.thumbnail}" alt="" />
                  </div>
                ` : ''}
              </div>
            `;
          },
        },
      }),
    ]);

    const searchInput = document.querySelector(this.searchInputClassName);
    searchInput.addEventListener('keydown', e => this.onSearchKeydown(e));
  }

  // https://www.algolia.com/doc/guides/building-search-ui/going-further/routing-urls/js/
  buildSearchUrlWithQuery(query) {
    return `${this.siteUrl}${this.siteUrl.endsWith('/') ? '' : '/'}search?${
      this.indexName
    }${encodeURIComponent('[query]')}=${encodeURIComponent(query)}`;
  }

  onSearchKeydown(e) {
    // If enter is hit, go to the search page
    if (e.key === KEY_CODES.ENTER) {
      const query = e.target.value;
      const searchUrl = this.buildSearchUrlWithQuery(query);
      window.location.replace(searchUrl);
    }
  }

  renderHits(renderOpts) {
    const { sendEvent } = renderOpts;
    const container = document.querySelector(renderOpts.widgetParams.container);

    if (!renderOpts.results || !renderOpts.results.query.trim()) {
      container.setAttribute('data-empty', true);
    } else {
      container.removeAttribute('data-empty');
    }

    if (!renderOpts.hits.length) {
      container.innerHTML = renderOpts.widgetParams.templates.empty(renderOpts.results);
    } else {
      const searchUrl = this.buildSearchUrlWithQuery(renderOpts.results.query);

      container.innerHTML = `
        <ul>
          ${renderOpts.hits
            .map(hit => `<li>${renderOpts.widgetParams.templates.item(hit)}</li>`)
            .join('\n')}

          <li>
            <a class="hit-all-results" href="${searchUrl.toString()}" tabindex="0">
              <p>
              <span>See all results</span>
              <svg class="hit-arrow" aria-hidden="true" focusable="false">
                <use xlink:href="#arrow-right"></use>
              </svg>
              </p>
            </a>
          </li>
        </ul>
      `;

      this.attachClickListeners(container, renderOpts.hits, sendEvent);
    }
  }

  attachClickListeners(container, hits, sendEvent) {
    container.querySelectorAll('.hit-link').forEach((link, index) => {
      link.addEventListener('click', () => {
        const hit = hits[index];
        if (hit) {
          sendEvent('click', hit, 'Inline Search Result Clicked');
        }
      });
    });
  }
}

export default InlineSearch;
